<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { vMaska } from 'maska/vue';
import { useDebounce } from '@vueuse/core';
import type { FieldSchema } from '../../../types';

const emits = defineEmits<{
  blur: [FocusEvent];
  focus: [FocusEvent];
  'update:modelValue': [string | null | undefined];
}>();

const props = withDefaults(
  defineProps<{
    debounceTime?: number;
    disabled?: boolean;
    error?: string | boolean;
    field?: FieldSchema;
    inProgress?: boolean;
    inputClass?: string;
    modelValue: string | undefined | null;
    placeholder?: string;
    preventTab?: boolean;
    required?: boolean;
  }>(),
  {
    debounceTime: 300,
    placeholder: '(999) 999-9999',
    preventTab: false,
  }
);

const _model = ref(props.modelValue || '');

const input = useDebounce(_model, props.debounceTime);

const activeModelProp = computed(() => props?.field?.model || props.modelValue);
const settings = computed(() => props?.field?.settings);
const disabledClass = computed(() => ({
  'pointer-events-none cursor-default bg-[#E2E8F0] text-[#7A889D]': props.disabled,
}));

watch(activeModelProp, (newValue) => (_model.value = newValue));
watch(input, () => emits('update:modelValue', _model.value));

function handleKeyDown(event: KeyboardEvent) {
  if (props.preventTab && event.key === 'Tab') return event.preventDefault();
}
</script>

<template>
  <div class="w-full">
    <input
      v-maska
      v-model="_model"
      type="tel"
      data-maska="['(##) ###-####', '(###) ###-####', '#(###) ###-####', '##(###) ###-####', '###(###) ###-####']"
      :class="[
        'h-9 w-full !border-gray-300 text-sm text-gray-700 focus:!border-green-500 focus:!ring-0',
        inputClass ?? 'rounded-md',
        { 'af-field-error !border-red-600': !!props.error },
        disabledClass,
      ]"
      :placeholder="settings?.placeholder || placeholder"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @keydown="handleKeyDown"
    />

    <ProgressBar
      v-if="inProgress"
      class="-mb-0.5 h-0.5 w-full"
      mode="indeterminate"
      pt:value="bg-brand-400"
    />
  </div>
</template>
